import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiSpellingBee } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

export default function SpellingBeeSolver() {
  const [letters, setLetters]   = useState('');
  const [center, setCenter]     = useState('');
  const [results, setResults]   = useState(null);
  const [pangrams, setPangrams] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSolve(e) {
    e.preventDefault();
    const l = letters.toLowerCase().replace(/[^a-z]/g, '');
    const c = center.toLowerCase().trim();
    if (l.length < 4) return setError('Enter at least 4 letters');
    if (!c || c.length !== 1) return setError('Enter a single center letter');
    if (!l.includes(c)) return setError('Center letter must be in your letters');
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiSpellingBee(l, c);
      setResults(data.results);
      setPangrams(data.pangrams || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  }

  const grouped = results
    ? results.reduce((acc, w) => { (acc[w.length] = acc[w.length] || []).push(w); return acc; }, {})
    : {};

  return (
    <div className="game-page">
      <PageSEO page="spelling-bee" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🐝</span>
          <h1>Spelling Bee Solver</h1>
          <p>Find all words using your 7 letters — center letter must appear in every word!</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="row g-3 mb-3">
              <div className="col-8">
                <label className="fw-bold mb-1">All Letters (4–7)</label>
                <input
                  className="big-input"
                  value={letters}
                  onChange={e => setLetters(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 7))}
                  placeholder="e.g. ACDEIL T"
                  maxLength={7}
                  autoFocus
                />
                <small className="text-muted">{letters.length}/7 letters entered</small>
              </div>
              <div className="col-4">
                <label className="fw-bold mb-1">Center Letter</label>
                <input
                  className="big-input text-center fw-bold"
                  style={{fontSize:'2rem', letterSpacing:4}}
                  value={center}
                  onChange={e => setCenter(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 1))}
                  placeholder="?"
                  maxLength={1}
                />
              </div>
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !letters || !center}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Solving…</> : '🐝 Find Words'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <p className="result-count">
              Found <strong>{results.length}</strong> word{results.length !== 1 ? 's' : ''}
              {pangrams.length > 0 && <> · <span style={{color:'#d97706'}}>🌟 {pangrams.length} pangram{pangrams.length !== 1 ? 's' : ''}</span></>}
            </p>
            {pangrams.length > 0 && (
              <div className="mb-3 p-3 rounded-3" style={{background:'#fef3c7'}}>
                <div className="fw-bold text-warning mb-1">🌟 Pangrams (uses all letters!)</div>
                <div className="words-cloud">
                  {pangrams.map(w => (
                    <DefinableWord key={w} word={w} className="result-badge" style={{background:'#d97706', color:'#fff', textDecoration:'none'}}>{w.toUpperCase()}</DefinableWord>
                  ))}
                </div>
              </div>
            )}
            {results.length === 0 ? (
              <p className="text-muted">No words found. Try different letters!</p>
            ) : (
              Object.keys(grouped).sort((a,b) => b - a).map(len => (
                <div key={len} className="mb-3">
                  <h6 className="fw-bold text-muted mb-2">{len}-letter words ({grouped[len].length})</h6>
                  <div className="words-cloud">
                    {grouped[len].map(w => (
                      <DefinableWord key={w} word={w} className="result-badge"
                        style={{...(pangrams.includes(w) ? {background:'#d97706', color:'#fff'} : {}), textDecoration:'none'}}>
                        {w.toUpperCase()}
                      </DefinableWord>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        <SeoContent game="spelling-bee" />
      </div>
    </div>
  );
}
